import { useCallback, useRef } from "react";

const nativeValue = Object.getOwnPropertyDescriptor(
  HTMLInputElement.prototype,
  "value"
)?.set;

const AUTOFILL_ANIMATION = "sky-credential-autofill";

function ensureAutofillStyle() {
  if (document.getElementById(AUTOFILL_ANIMATION)) return;
  const style = document.createElement("style");
  style.id = AUTOFILL_ANIMATION;
  style.textContent = `
    @keyframes ${AUTOFILL_ANIMATION} { from {} to {} }
    input[name="username"]:-webkit-autofill,
    input[name="user_name"]:-webkit-autofill,
    input[name="password"]:-webkit-autofill {
      animation-name: ${AUTOFILL_ANIMATION};
      animation-duration: 1ms;
    }
  `;
  document.head.appendChild(style);
}

function usernameInput(root: ParentNode) {
  return root.querySelector(
    '[name="user_name"], [name="username"]'
  ) as HTMLInputElement | null;
}

function passwordInput(root: ParentNode) {
  return root.querySelector('[name="password"]') as HTMLInputElement | null;
}

function readCredentials(root: ParentNode) {
  const username = usernameInput(root)?.value.trim() || "";
  const password = passwordInput(root)?.value || "";
  return { username, password };
}

function writeValue(input: HTMLInputElement, value: string) {
  nativeValue?.call(input, value);
}

function isCredentialField(target: EventTarget | null) {
  return (
    target instanceof HTMLInputElement &&
    (target.name === "password" ||
      target.name === "username" ||
      target.name === "user_name")
  );
}

function fieldKey(input: HTMLInputElement) {
  return input.name === "password" ? "password" : "username";
}

function fieldValue(input: HTMLInputElement) {
  return input.name === "password" ? input.value : input.value.trim();
}

function isAutofilled(input: HTMLInputElement | null) {
  if (!input) return false;
  try {
    return input.matches(":-webkit-autofill");
  } catch {
    return false;
  }
}

/** Append or delete of a single character. A saved login replaces the whole value. */
function isOneStepEdit(previous: string, next: string) {
  if (next === previous) return true;
  if (Math.abs(next.length - previous.length) > 1) return false;
  return next.startsWith(previous) || previous.startsWith(next);
}

/**
 * Keystrokes, paste, and delete are manual. A saved-login pick fills the
 * whole password in one step, so a one-character change is never autofill.
 */
function isManualEdit(event: Event, previous: string, next: string) {
  const inputType =
    event.type === "input" ? (event as InputEvent).inputType || "" : "";
  if (
    inputType === "insertText" ||
    inputType === "insertCompositionText" ||
    inputType === "insertFromPaste" ||
    inputType === "insertFromDrop" ||
    inputType === "insertFromYank" ||
    inputType.startsWith("delete") ||
    inputType === "historyUndo" ||
    inputType === "historyRedo"
  ) {
    return true;
  }
  return Math.abs(next.length - previous.length) <= 1;
}

function submitLogin(root: HTMLElement) {
  if (root instanceof HTMLFormElement && typeof root.requestSubmit === "function") {
    const button = root.querySelector(
      "button[type='submit']"
    ) as HTMLButtonElement | null;
    if (button?.disabled) return;
    try {
      root.requestSubmit();
      return;
    } catch {
      // A disabled submit button makes requestSubmit throw.
    }
  }
  const button = root.querySelector(
    "button[type='submit'], a.btn-login"
  ) as HTMLButtonElement | null;
  if (!button || button.disabled) return;
  button.click();
}

/**
 * Submits login only after the browser fills a saved username and password
 * (the user picked a saved login). Typed, pasted, and deleted values wait
 * for the Login button. Captcha-on sites pass enabled=false.
 */
export function useCredentialAutofillLogin(enabled: boolean) {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const nodeRef = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((node: HTMLElement | null) => {
    if (nodeRef.current === node) return;
    cleanupRef.current?.();
    cleanupRef.current = null;
    nodeRef.current = node;
    if (!node) return;

    ensureAutofillStyle();
    const seen = readCredentials(node);
    let lastSubmitted = "";
    let timer = 0;
    let armed = false;

    const stopTimer = () => {
      if (!timer) return;
      window.clearTimeout(timer);
      timer = 0;
    };

    const trySubmit = () => {
      timer = 0;
      if (!enabledRef.current || !node.isConnected) return;
      const { username, password } = readCredentials(node);
      // Ignore a fill that arrives before the user focuses a field
      // (browser may restore a saved login on page load).
      if (!armed) {
        seen.username = username;
        seen.password = password;
        return;
      }
      if (!username || !password) return;
      if (username === seen.username && password === seen.password) return;
      const passEl = passwordInput(node);
      const userEl = usernameInput(node);
      const passwordFilledByBrowser = !isOneStepEdit(seen.password, password);
      const bothAutofilled =
        isAutofilled(userEl) &&
        isAutofilled(passEl) &&
        password !== seen.password;
      // Typing changes the password one character at a time. A saved login
      // replaces it in one step, or marks both fields as browser-filled.
      if (!passwordFilledByBrowser && !bothAutofilled) {
        seen.username = username;
        seen.password = password;
        return;
      }
      const key = `${username}\n${password}`;
      if (key === lastSubmitted) return;
      lastSubmitted = key;
      seen.username = username;
      seen.password = password;
      window.setTimeout(() => {
        if (!enabledRef.current || !node.isConnected) return;
        const userEl = usernameInput(node);
        const passEl = passwordInput(node);
        if (!userEl || !passEl) return;
        if (userEl.value.trim() !== username) writeValue(userEl, username);
        if (passEl.value !== password) writeValue(passEl, password);
        submitLogin(node);
      }, 0);
    };

    const schedule = () => {
      stopTimer();
      timer = window.setTimeout(trySubmit, 80);
    };

    const onEdit = (event: Event) => {
      if (!isCredentialField(event.target)) return;
      const input = event.target as HTMLInputElement;
      const key = fieldKey(input);
      const previous = seen[key];
      const next = fieldValue(input);
      if (isManualEdit(event, previous, next)) {
        const bulk = Math.abs(next.length - previous.length) > 1;
        // Chrome sometimes reports a saved password as insertText. Keep the
        // previous value so the fill still counts as a browser replacement.
        if (bulk && isAutofilled(input)) {
          schedule();
          return;
        }
        seen[key] = next;
        stopTimer();
        return;
      }
      schedule();
    };

    const onAnimation = (event: AnimationEvent) => {
      if (event.animationName !== AUTOFILL_ANIMATION) return;
      if (!isCredentialField(event.target)) return;
      schedule();
    };

    const onFocusIn = (event: Event) => {
      if (!isCredentialField(event.target)) return;
      armed = true;
    };

    node.addEventListener("input", onEdit, true);
    node.addEventListener("change", onEdit, true);
    node.addEventListener("animationstart", onAnimation, true);
    node.addEventListener("focusin", onFocusIn);
    cleanupRef.current = () => {
      stopTimer();
      node.removeEventListener("input", onEdit, true);
      node.removeEventListener("change", onEdit, true);
      node.removeEventListener("animationstart", onAnimation, true);
      node.removeEventListener("focusin", onFocusIn);
    };
  }, []);
}
